import {
  ArrowLeft,
  Bell,
  CaretDown,
  CaretUp,
  Check,
  GearSix,
} from '@phosphor-icons/react'
import { useCallback, useMemo, useState } from 'react'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import '../notifications.css'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { EmptyState } from '@/features/discovery/components/empty-state'
import { formatNumber } from '@/lib/i18n/format'

import { NotificationTile } from '../components/notification-tile'
import { NotificationNotice } from '../components/notice'
import { NotificationsPullToRefresh } from '../components/pull-to-refresh'
import { ProximityCard } from '../components/proximity-card'
import { NativePushCard } from '../components/native-push-card'
import { PushCard } from '../components/push-card'
import { PushPreferences } from '../components/push-preferences'
import { NotificationsRefreshButton } from '../components/refresh-button'
import { NotificationsRefreshStatus } from '../components/refresh-status'
import { useNotificationsIsDesktop } from '../hooks/use-is-desktop'
import { useNotificationsLocale } from '../hooks/use-locale'
import { useNativePush } from '../hooks/use-native-push'
import { useNotifications } from '../hooks/use-notifications'
import { useProximitySettings } from '../hooks/use-proximity-settings'
import { usePushPreferences } from '../hooks/use-push-preferences'
import { useUnreadNotificationCount } from '../hooks/use-unread-notification-count'
import { useWebPush } from '../hooks/use-web-push'
import { notificationHref } from '../links'
import { isUnread, type Notification } from '../types'

/**
 * `/notifications` (UI_PARITY §3.15): the in-app inbox, explicit mark-read
 * actions, six push categories and the browser push opt-in.
 *
 * The screen owns explicit request lifecycles (D-042). The unread hook keeps
 * the header/shell badge counter current on route changes, focus and a 15 s
 * visible interval while this screen is open. D-053 splits refresh affordances:
 * pull-to-refresh below 768 px, a labelled button above.
 */
export function NotificationsScreen() {
  const { t } = useTranslation()
  const locale = useNotificationsLocale()
  const isDesktop = useNotificationsIsDesktop()
  const navigate = useNavigate()
  const {
    items,
    phase,
    refreshing,
    error,
    status,
    markingAll,
    refresh,
    markRead,
    markAllRead,
    dismissStatus,
  } = useNotifications()
  const pushPreferences = usePushPreferences()
  const proximity = useProximitySettings()
  const webPush = useWebPush()
  const nativePush = useNativePush()
  const { refresh: refreshBadge } = useUnreadNotificationCount()
  // Deep link from the new settings menu: /notifications?settings=1 opens the
  // notification settings drawer directly (D-099).
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const [settingsOpen, setSettingsOpen] = useState(
    searchParams.get('settings') === '1',
  )
  // Coming from the settings menu, the corner arrow returns one level up.
  const cameFromSettings =
    (location.state as { from?: string } | null)?.from === 'settings'
  const backTo =
    settingsOpen && cameFromSettings ? '/settings' : '/home'

  const unreadCount = useMemo(
    () => items.filter(isUnread).length,
    [items],
  )
  // D-099 maintainer revision: the inbox leads with unread notifications;
  // the read ones live behind a collapsed disclosure.
  const unreadItems = useMemo(() => items.filter(isUnread), [items])
  const readItems = useMemo(
    () => items.filter((notification) => !isUnread(notification)),
    [items],
  )
  const [previousOpen, setPreviousOpen] = useState(false)

  const handleOpen = useCallback(
    (notification: Notification) => {
      if (isUnread(notification)) {
        void markRead(notification.id).then(() => refreshBadge())
      }
      const href = notificationHref(notification)
      if (href) navigate(href)
    },
    [markRead, navigate, refreshBadge],
  )

  async function handleMarkAllRead() {
    await markAllRead()
    refreshBadge()
  }

  const summary = useMemo(() => {
    if (unreadCount === 0) return t('notifications.summary.caughtUp')
    return t('notifications.summary.unread', {
      count: unreadCount,
      formatted: formatNumber(unreadCount, locale),
    })
  }, [locale, t, unreadCount])

  return (
    <section aria-labelledby="notifications-title" className="content-width pt-[var(--safe-top)] pb-10">
      <div className="mx-auto w-full max-w-[920px] px-0.5 md:px-0">
        <header className="flex items-center gap-2 py-3">
          <Link
            className="grid size-11 shrink-0 place-items-center rounded-full text-deep hover:bg-mint"
            aria-label={t('common.back')}
            to={backTo}
          >
            <ArrowLeft size={22} weight="bold" aria-hidden="true" />
          </Link>
          <h1
            id="notifications-title"
            className="mr-auto min-w-0 text-[28px] leading-none"
          >
            {t('screens.notifications.title')}
          </h1>
          <button
            type="button"
            className={`grid size-11 shrink-0 place-items-center rounded-full hover:bg-mint ${
              settingsOpen ? 'bg-mint text-teal' : 'text-deep'
            }`}
            aria-expanded={settingsOpen}
            aria-controls="push-settings"
            data-testid="push-settings-toggle"
            aria-label={t('notifications.push.settings')}
            onClick={() => setSettingsOpen((value) => !value)}
          >
            <GearSix size={22} weight="bold" aria-hidden="true" />
          </button>
          {isDesktop ? (
            <NotificationsRefreshButton
              onRefresh={refresh}
              refreshing={refreshing}
            />
          ) : null}
        </header>

        <div className="flex flex-wrap items-center gap-2">
          <p className="mr-auto text-[15px] leading-relaxed text-muted">
            {summary}
          </p>
          {/* D-099 maintainer revision: nothing new to mark — hide it. */}
          {phase === 'ready' && unreadCount > 0 ? (
            <button
              type="button"
              className="btn-ghost min-h-11 px-2 text-sm"
              onClick={() => void handleMarkAllRead()}
              disabled={markingAll}
              aria-busy={markingAll}
            >
              <Check size={18} weight="bold" aria-hidden="true" />
              {markingAll
                ? t('notifications.actions.markingAllRead')
                : t('notifications.actions.markAllRead')}
            </button>
          ) : null}
        </div>

        {status ? (
          <div className="mt-3">
            <NotificationNotice
              tone={status.tone}
              messageKey={status.messageKey}
              onDismiss={dismissStatus}
            />
          </div>
        ) : null}

        {/* Android push enablement must be discoverable without opening the
            settings drawer: the card disappears once the device is
            subscribed (D-097). */}
        {!settingsOpen && nativePush.supported && !nativePush.subscribed ? (
          <div className="mt-3">
            <NativePushCard state={nativePush} />
          </div>
        ) : null}

        {settingsOpen ? (
          <div id="push-settings">
            {nativePush.supported ? (
              <NativePushCard state={nativePush} />
            ) : (
              <PushCard state={webPush} />
            )}

            <div className="mt-3 rounded-card border border-line bg-white p-4 md:p-5">
              <PushPreferences state={pushPreferences} />
            </div>

            <div className="mt-3 rounded-card border border-line bg-white p-4 md:p-5">
              <ProximityCard state={proximity} />
            </div>
          </div>
        ) : null}

        {/* D-099 maintainer revision: the notification settings are their own
            view on mobile (no inbox list under the cards); the settings
            deep link lands here and the gear toggles back to the inbox. */}
        {!settingsOpen ? (
          <>
            <div className="mt-6 flex items-center justify-between gap-3">
              <h2 className="text-lg font-extrabold">
                {t('notifications.list.sectionLabel')}
              </h2>
            </div>

            <NotificationsRefreshStatus visible={refreshing} />

            <NotificationsPullToRefresh
              className="mt-3"
              onRefresh={refresh}
              refreshing={refreshing}
              disabled={isDesktop}
            >
              {error && items.length > 0 ? (
                <div className="mb-3">
                  <NotificationNotice
                    tone="error"
                    messageKey="notifications.status.refreshError"
                    onRetry={refresh}
                  />
                </div>
              ) : null}

              {phase === 'loading' ? (
                <LoadingState labelKey="notifications.list.loading" />
              ) : phase === 'error' ? (
                <ErrorState
                  error={error}
                  titleKey="notifications.list.errorTitle"
                  bodyKey="notifications.list.errorBody"
                  onRetry={refresh}
                />
              ) : items.length === 0 ? (
                <EmptyState
                  compact
                  icon={<Bell size={26} weight="bold" aria-hidden="true" />}
                  titleKey="notifications.list.emptyTitle"
                  bodyKey="notifications.list.emptyBody"
                />
              ) : (
                <div className="space-y-3">
                  {unreadItems.length > 0 ? (
                    <ul
                      className="space-y-3"
                      aria-label={t('notifications.list.sectionLabel')}
                    >
                      {unreadItems.map((notification) => (
                        <li key={notification.id}>
                          <NotificationTile
                            notification={notification}
                            onOpen={handleOpen}
                          />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted">
                      {t('notifications.list.unreadEmpty')}
                    </p>
                  )}

                  {readItems.length > 0 ? (
                    <div>
                      <button
                        type="button"
                        className="btn-ghost min-h-11 w-full justify-between px-2 text-sm"
                        aria-expanded={previousOpen}
                        aria-controls="previous-notifications"
                        data-testid="previous-notifications-toggle"
                        onClick={() => setPreviousOpen((value) => !value)}
                      >
                        {previousOpen ? (
                          <CaretUp
                            size={16}
                            weight="bold"
                            aria-hidden="true"
                          />
                        ) : (
                          <CaretDown
                            size={16}
                            weight="bold"
                            aria-hidden="true"
                          />
                        )}
                        {t('notifications.list.previous', {
                          count: readItems.length,
                        })}
                      </button>
                      {previousOpen ? (
                        <ul
                          id="previous-notifications"
                          className="mt-3 space-y-3"
                          aria-label={t('notifications.list.previous', {
                            count: readItems.length,
                          })}
                        >
                          {readItems.map((notification) => (
                            <li key={notification.id}>
                              <NotificationTile
                                notification={notification}
                                onOpen={handleOpen}
                              />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </NotificationsPullToRefresh>
          </>
        ) : null}
      </div>
    </section>
  )
}
